import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { EmployeePanel } from './EmployeePanel/EmployeePanel';
import { MapPanel } from './MapPanel/MapPanel';
import { useTrackingFilters } from '../hooks/useTrackingFilters';
import { useUserActivity } from '../hooks/useUserActivity';
import { useUserTracking } from '../hooks/useUserTracking';
import { useMapPlayback } from '../hooks/useMapPlayback';
import { useAuth } from '@/hooks/useAuth';
import { apiConfig } from '@/config/apiConfig';
import { canEditTracking } from '../utils/permissions';
import {
  buildEffectiveSegmentPoints,
  effectiveSegmentDistanceMeters,
} from '../utils/mapHelpers';
import type { MapViewMode, GeoPoint } from '../types/tracking';

export const LiveTracking: React.FC = () => {
  const { filters, setDate, toggleProgram, setStatus, setLocationStatus, setSearchQuery } = useTrackingFilters();
  const { activities, loading: actLoading, error: actError, refetch } = useUserActivity(filters.date, filters.programs, filters.locationStatus);
  const { trackingData, loading: trackLoading, fetchTracking, clearTracking } = useUserTracking();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // OSRM path-correction feature flag. Off in envs where the Supabase
  // Edge Functions aren't deployed yet — playback and totals fall back
  // to raw segments in that case. Also toggled off when the reviewer
  // doesn't have the editor role, but for playback / total display the
  // role gate doesn't apply — a viewer still sees the corrected path
  // if the backend has one (matches the Reports view's behavior). So
  // playback uses `osrmEnabled` on its own, not `canEditSegments`.
  const osrmEnabled = apiConfig.features.enableOsrmPathCorrection;

  // Derive map view mode
  const viewMode: MapViewMode = selectedIds.size === 0 ? 'aggregated' : 'route';

  // Collect the animation path for playback (single-employee only). When
  // a segment has an OSRM correction applied AND the feature is on, we
  // substitute the corrected coordinates so the playback marker follows
  // the road-snapped path instead of the raw drift. Segments from
  // different sessions need globally-unique `segment_index` values so
  // the playback hook treats the gap between two sessions as a break
  // (no haversine bridge across the off-duty interval).
  const allPoints = useMemo<GeoPoint[]>(() => {
    if (selectedIds.size !== 1) return [];
    const userId = [...selectedIds][0];
    const data = trackingData.get(userId);
    if (!data) return [];
    let segmentOffset = 0;
    const points: GeoPoint[] = [];
    for (const session of data.sessions) {
      let maxSegInSession = -1;
      for (const segment of session.segments) {
        const effective = buildEffectiveSegmentPoints(segment, osrmEnabled);
        for (const p of effective) {
          const segIdx = p.segment_index ?? segment.index;
          if (segIdx > maxSegInSession) maxSegInSession = segIdx;
          points.push({ ...p, segment_index: segIdx + segmentOffset });
        }
      }
      segmentOffset += maxSegInSession + 1;
    }
    return points.sort(
      (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
    );
  }, [selectedIds, trackingData, osrmEnabled]);

  // Day total — same OSRM-aware rule the backend's get_report() uses
  // for `total_modified_distance_meters`: sum `osrm_total_distance` for
  // segments where a correction is applied, otherwise the raw distance.
  // Keeps the "Total Distance Covered" readout under the playback
  // timeline consistent with the Reports view.
  const totalDistanceMeters = useMemo<number | undefined>(() => {
    if (selectedIds.size !== 1) return undefined;
    const userId = [...selectedIds][0];
    const data = trackingData.get(userId);
    if (!data) return undefined;
    return data.sessions.reduce(
      (sum, s) =>
        sum +
        s.segments.reduce(
          (segSum, seg) => segSum + effectiveSegmentDistanceMeters(seg, osrmEnabled),
          0,
        ),
      0,
    );
  }, [selectedIds, trackingData, osrmEnabled]);

  const { playbackState, play, pause, stop, setSpeed, seekTo } = useMapPlayback(
    allPoints,
    totalDistanceMeters,
  );

  // Fetch tracking when employees are selected
  useEffect(() => {
    for (const userId of selectedIds) {
      if (!trackingData.has(userId)) {
        fetchTracking(userId, filters.date);
      }
    }
  }, [selectedIds, filters.date, fetchTracking, trackingData]);

  // Keep a ref to stop so the clear effect doesn't re-fire when stop's reference changes
  const stopRef = useRef(stop);
  stopRef.current = stop;

  // Clear selections when date/program values change
  // Use join(',') so comparison is by value, not array reference
  const programsKey = filters.programs.join(',');
  useEffect(() => {
    setSelectedIds(new Set());
    clearTracking();
    stopRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date, programsKey, filters.locationStatus, clearTracking]);

  const handleSelect = useCallback((userId: string, multiSelect: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (multiSelect) {
        if (next.has(userId)) {
          next.delete(userId);
        } else {
          next.add(userId);
        }
      } else {
        if (next.size === 1 && next.has(userId)) {
          // Deselect if clicking the only selected one
          next.clear();
        } else {
          next.clear();
          next.add(userId);
        }
      }
      return next;
    });
    stop(); // Reset playback on selection change
  }, [stop]);

  const [panelOpen, setPanelOpen] = useState(true);

  // Role gate for the OSRM correction menu / preview panel. The
  // feature flag is already applied via `osrmEnabled` above; here we
  // fold in the LOCATION.TRACKING_EDITOR / ADMIN check so a viewer
  // never even sees the right-click menu. The server still enforces
  // the same role on every /match-route, /insert-osrm-path, and
  // /sync-osrm-data call.
  const { user } = useAuth();
  const canEditSegments = osrmEnabled && canEditTracking(user);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Map - central, fills available space */}
      <div className="flex-1">
        <MapPanel
          viewMode={viewMode}
          activities={activities}
          selectedIds={selectedIds}
          trackingData={trackingData}
          trackingLoading={trackLoading}
          playbackState={playbackState}
          onPlay={play}
          onPause={pause}
          onStop={stop}
          onSetSpeed={setSpeed}
          onSeekTo={seekTo}
          onSelect={handleSelect}
          panelOpen={panelOpen}
          onTogglePanel={() => setPanelOpen(prev => !prev)}
          canEditSegments={canEditSegments}
          osrmFeatureEnabled={osrmEnabled}
          trackingDate={filters.date}
          onOsrmDecisionCommitted={(userId) => fetchTracking(userId, filters.date)}
        />
      </div>

      {/* Right panel - Employee filters (collapsible) */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          panelOpen ? 'w-[360px] min-w-[320px] xl:w-[420px] 2xl:w-[480px]' : 'w-0'
        }`}
      >
        {panelOpen && (
          <EmployeePanel
            filters={filters}
            activities={activities}
            loading={actLoading}
            error={actError}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onDateChange={setDate}
            onToggleProgram={toggleProgram}
            onStatusChange={setStatus}
            onLocationStatusChange={setLocationStatus}
            onSearchChange={setSearchQuery}
            onRetry={refetch}
          />
        )}
      </div>
    </div>
  );
};
